import React, { useState, useEffect, useCallback } from 'react';
import { Quote, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { reviews } from '../mock/mock';

export default function ReviewsCarousel() {
  const items = reviews.items;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((dir) => {
    setIndex((i) => (i + dir + items.length) % items.length);
  }, [items.length]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => go(1), 6000);
    return () => clearInterval(t);
  }, [go, paused]);

  return (
    <section className="bg-sage-900 text-white py-24 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_30%_20%,white,transparent_60%)]" />
      <div className="max-w-[900px] mx-auto px-5 text-center relative">
        <div className="flex justify-center gap-1 mb-4">
          {[...Array(5)].map((_, i) => (
            <Star key={i} size={18} className="fill-sage-400 text-sage-400" />
          ))}
        </div>
        <h2 className="text-4xl md:text-5xl font-serif mb-3">{reviews.title}</h2>
        <p className="text-cream/70 mb-12">{reviews.subtitle}</p>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-700 ease-out"
              style={{ transform: `translateX(-${index * 100}%)` }}
            >
              {items.map((r, i) => (
                <div key={i} className="min-w-full px-2">
                  <div className="bg-white text-ink rounded-2xl shadow-2xl px-8 md:px-14 py-12 max-w-2xl mx-auto">
                    <Quote className="mx-auto text-sage-400 mb-6" size={40} />
                    <p className="italic text-lg leading-relaxed text-ink/80">{r.quote}</p>
                    <div className="flex justify-center gap-1 my-6">
                      {[...Array(5)].map((_, s) => (
                        <Star key={s} size={16} className="fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="font-serif text-xl">{r.author}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Arrows */}
          <button
            onClick={() => go(-1)}
            aria-label="Previous"
            className="absolute left-0 md:-left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-sage-500 border border-white/20 flex items-center justify-center transition-all"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => go(1)}
            aria-label="Next"
            className="absolute right-0 md:-right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-sage-500 border border-white/20 flex items-center justify-center transition-all"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2.5 mt-8">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Go to review ${i + 1}`}
              className={`h-2.5 rounded-full transition-all ${
                i === index ? 'w-8 bg-sage-400' : 'w-2.5 bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
